import {
  BlockVariant,
  ButtonBlockProps,
  CommentBlockProps,
  DynamicBlock,
  DynamicBlocks,
  InputTextareaBlockProps,
  TextBlockProps,
} from '@/components/block-builder/block-builder.types';
import { PaaTaskDetails } from '@/types';
import { Injectable } from '@angular/core';
import _ from 'lodash';
import { BehaviorSubject, map, Observable } from 'rxjs';

interface getPreDraftPaaBlocksProps {
  save?: () => void;
  rest?: () => void;
}

interface getInspectPaaBlocksProps {
  refreshCount: number;
  status?: string;
  refresh?: () => void;
  accessHistory?: () => void;
  downloadSigned?: () => void;
  downloadExcelModel?: () => void;
}

interface getDraftPaaBlocksProps {
  refreshCount: number;
  isAltered?: Observable<boolean>;
  save?: () => void;
  rest?: () => void;
  validate?: () => void;
  remove?: () => void;
  accessHistory?: () => void;
}

interface getFlowablePaaBlocksProps {
  refreshCount: number;
  task?: PaaTaskDetails;
  save?: () => void;
  rest?: () => void;
  remove?: () => void;
  validate?: (value?: string | null) => void;
  accessHistory?: () => void;
  //ac validation **********************
  openDownloadDialog?: () => void;
  openUploadDialog?: () => void;
  // **********************************
  reject?: () => void;
  comment?: BehaviorSubject<string>;
  onCommentChange?: (value: string) => void;
  error?: BehaviorSubject<string | null>;
}

enum TaskVariant {
  VALIDATION = 'Validation (AC)',
  PUBLICATION = '(AC) Publication',
  RECTIFICATION = 'Rectification (AC)',
}

@Injectable({
  providedIn: 'root',
})
export class PaaWorkflowBlocksService {
  static mapToFrenchValues(value: string) {
    if (value == 'Validate') return 'Valider';
    if (value == 'Reject') return 'Rejeter';
    if (value == 'Publish') return 'Publier';
    return value;
  }

  accessHistoryButton = (accessHistory?: () => void): DynamicBlock => {
    return {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'paa.actions.access_history',
        onClick: accessHistory,
        severity: 'secondary',
      },
    };
  };

  refreshedLabel = (refreshCount: number): DynamicBlock<TextBlockProps> => {
    if (refreshCount === 0)
      return {
        variant: BlockVariant.CUSTOM,
      };
    else
      return {
        variant: BlockVariant.TEXT,
        props: {
          blocks: [
            {
              content: refreshCount === 1 ? 'Ce document a été actualisé une seule fois' : `Ce document a été actualisé ${refreshCount} fois`,
              class: 'text-[1rem] font-thin opacity-70 no-select italic',
            },
          ],
          orientation: 'horizontal',
          gap: 1,
        },
        class: 'w-fit mx-auto',
      };
  };

  getInspectPaaBlocks = ({
    status,
    refreshCount,
    accessHistory,
    downloadSigned,
    downloadExcelModel,
    refresh,
  }: getInspectPaaBlocksProps): DynamicBlocks[] => {
    const finalBlocks: DynamicBlocks[] = [];

    const refreshButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'paa.actions.refresh',
        onClick: () => refresh?.(),
      },
    };

    const downloadSignedButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'paa.actions.download_model',
        onClick: () => downloadSigned?.(),
        severity: 'info',
      },
    };

    const downloadExcelModelButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'paa.actions.download_excel_model',
        onClick: () => downloadExcelModel?.(),
        severity: 'info',
      },
    };

    finalBlocks.push({});
    if (status === 'Publie') {
      finalBlocks[0]['0'] = [refreshButton];
    }
    if (downloadSigned) finalBlocks[0]['1'] = [downloadSignedButton];
    if (downloadExcelModel) finalBlocks[0]['2'] = [downloadExcelModelButton];
    if (_.isEqual(finalBlocks[0], {})) finalBlocks.splice(0, 1);

    finalBlocks.push({
      '0': [this.accessHistoryButton(accessHistory)],
      '1': [this.refreshedLabel(refreshCount)],
    });

    return finalBlocks;
  };

  getPreDraftPaaBlocks = ({ save, rest }: getPreDraftPaaBlocksProps): DynamicBlocks => {
    const saveButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Enregistrer',
        onClick: () => save?.(),
      },
    };
    const resetButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Réinitialiser',
        onClick: rest,
        severity: 'secondary',
      },
    };
    return {
      '1': [saveButton],
      '2': [resetButton],
    };
  };

  getDraftPaaBlocks = ({ refreshCount, isAltered, save, rest, validate, remove, accessHistory }: getDraftPaaBlocksProps): DynamicBlocks[] => {
    const saveButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Enregistrer',
        onClick: () => save?.(),
        disabled: isAltered?.pipe(map(value => !value)),
      },
    };

    const deleteButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Supprimer',
        onClick: remove,
        severity: 'danger',
      },
    };

    const startWorkflowButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Passer pour validation',
        onClick: validate,
      },
    };

    return [
      {
        '0': [saveButton, startWorkflowButton], //Enregistrer + Passer pour validation
        // '2': blocks['2'], // reset
        // '3': [deleteButton],
      },
      {
        '0': [this.accessHistoryButton(accessHistory)],
        '1': [this.refreshedLabel(refreshCount)],
      },
    ];
  };

  getFlowablePaaBlocks = ({
    refreshCount,
    task,
    validate,
    accessHistory,
    openDownloadDialog,
    openUploadDialog,
    reject,
    comment,
    onCommentChange,
    error,
  }: getFlowablePaaBlocksProps): DynamicBlocks[] => {
    const blocks: DynamicBlock[] = [];
    let renderTextarea = false;

    //detect status
    if (task?.taskName == TaskVariant.VALIDATION) {
      //detect actions
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          if (`${step.validationValue}` == 'true') {
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: `Télécharger`,
                severity: 'success',
                onClick: openDownloadDialog,
              },
            });
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: `Déposer`,
                severity: 'secondary',
                onClick: openUploadDialog,
              },
            });
          } else {
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: PaaWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
                onClick: () => reject?.(),
              },
            });
          }
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.PUBLICATION) {
      blocks.push({
        variant: BlockVariant.BUTTON,
        props: {
          label: 'Publier',
          onClick: () => validate?.(),
        },
      });
    } else if (task?.taskName == TaskVariant.RECTIFICATION) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: PaaWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => validate?.(null),
            },
          });
        });
    }

    const textareablock: DynamicBlock<InputTextareaBlockProps> = {
      variant: BlockVariant.INPUT_TEXTAREA,
      props: {
        label: 'Commentaire',
        rows: 5,
        value: comment?.getValue(),
        placeholder: 'Saisissez un commentaire',
        onChange: (value: string) => onCommentChange?.(value),
        error: error,
      },
    };

    const commentBlock: DynamicBlock<CommentBlockProps> = {
      variant: BlockVariant.COMMENT,
      class: 'w-full',
      props: {
        label: 'Commentaire',
        content: task?.traceability?.comment || '',
        date: task?.traceability?.dateComment || '',
        class: 'w-full border p-2 rounded-md',
      },
    };

    const finalBlocks: DynamicBlocks = {};
    if (task?.traceability?.comment) finalBlocks['0'] = [commentBlock];
    if (blocks.length > 0) finalBlocks['1'] = blocks;
    if (renderTextarea) finalBlocks['2'] = [textareablock];
    return [
      finalBlocks,
      {
        '0': [this.accessHistoryButton(accessHistory)],
        '1': [this.refreshedLabel(refreshCount)],
      },
    ];
  };
}
